import { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { NAV_ITEMS, GROUP_ORDER, GROUP_LABELS, type NavItem } from '../consts/navigation';
import { useCollapsedNavGroups } from '../hooks/useCollapsedNavGroups';
import type { IUser } from '@mirasoles/shared';
import { hasRole } from '../lib/permissions';
import { Icon, ThemeToggle, ToastContainer } from '@metrodev/core-web';
import { NotificationBell } from './notifications/NotificationBell';
import { UserAvatar } from './profile/UserAvatar';
import { normalizeForSearch } from '../lib/normalize-text';
import { NotificationToastContainer } from './notifications/NotificationToast';

function pageTitle(pathname: string): { title: string; sub: string } {
  if (pathname === '/') return { title: 'Dashboard', sub: 'Resumen del sistema' };
  if (pathname.startsWith('/users/new')) return { title: 'Nuevo usuario', sub: 'Gestión de accesos' };
  if (pathname.match(/^\/users\/.+/)) return { title: 'Editar usuario', sub: 'Gestión de accesos' };
  if (pathname.startsWith('/users')) return { title: 'Usuarios', sub: 'Gestión de accesos' };
  if (pathname.startsWith('/notifications')) return { title: 'Notificaciones', sub: 'Avisos del sistema' };
  return { title: '', sub: '' };
}


function NavItemLink({ item, onNavClick }: { item: NavItem; onNavClick?: () => void }) {
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      onClick={onNavClick}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '10px 12px',
        borderRadius: 10,
        fontSize: 13.5,
        fontWeight: isActive ? 600 : 500,
        textDecoration: 'none',
        transition: 'background 0.15s',
        background: isActive ? 'var(--color-sidebar-active)' : 'transparent',
        color: isActive ? 'var(--color-sidebar-text)' : 'var(--color-sidebar-border)',
      })}
      onMouseOver={(e) => {
        const el = e.currentTarget;
        if (!el.getAttribute('aria-current')) el.style.background = 'rgba(255,255,255,0.06)';
      }}
      onMouseOut={(e) => {
        const el = e.currentTarget;
        if (!el.getAttribute('aria-current')) el.style.background = 'transparent';
      }}
    >
      <Icon name={item.icon} size={18} />
      {item.label}
    </NavLink>
  );
}

export function SidebarContent({ user, logout, onNavClick }: {
  user: IUser | null;
  logout: () => void;
  onNavClick?: () => void;
}) {
  const location = useLocation();
  const { collapsed, toggle: toggleGroup } = useCollapsedNavGroups(user?.id);
  const [navSearch, setNavSearch] = useState('');

  const visibleItems = useMemo(
    () => NAV_ITEMS.filter((item) =>
      item.exactRoles !== undefined
        ? !!user && item.exactRoles.includes(user.role)
        : item.minRole === undefined || hasRole(user, item.minRole),
    ),
    [user],
  );

  // Con búsqueda activa se ignora el agrupamiento/colapso y se muestra una
  // lista plana de resultados — evita tener que expandir cada grupo a mano
  // para encontrar un módulo.
  const searchResults = useMemo(() => {
    const query = normalizeForSearch(navSearch.trim());
    if (!query) return null;
    return visibleItems.filter((item) => normalizeForSearch(item.label).includes(query));
  }, [visibleItems, navSearch]);

  const groupedItems = useMemo(() => {
    // `item.group` es de tipo `NavGroup`, y `navigation.test.ts` verifica que
    // todo item tenga un grupo de `GROUP_ORDER` — no hace falta fallback.
    return GROUP_ORDER
      .map((group) => ({ group, items: visibleItems.filter((i) => i.group === group) }))
      .filter((section) => section.items.length > 0);
  }, [visibleItems]);

  // Grupo al que pertenece la ruta activa: se fuerza expandido mientras se está
  // dentro de él (aunque el usuario lo tenga colapsado en su preferencia), para
  // que el item activo nunca quede oculto. Se resuelve por render, así que
  // también aplica al navegar dentro de la SPA, no sólo al montar. Se elige el
  // match más específico (path más largo) para rutas anidadas.
  const activeGroup = useMemo(() => {
    const matches = NAV_ITEMS.filter(
      (i) =>
        i.path !== '/' &&
        (location.pathname === i.path || location.pathname.startsWith(i.path + '/')),
    );
    matches.sort((a, b) => b.path.length - a.path.length);
    return matches[0]?.group;
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{ padding: '22px 15px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px 24px' }}>
          <img
            src="/mirasoles-logo.png"
            alt="Mirasoles"
            style={{ height: 36, width: 'auto', filter: 'var(--logo-filter-sidebar)' }}
          />
        </div>
      </div>

      {/* Buscador de módulos */}
      <div style={{ padding: '0 15px 10px' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none', color: 'var(--color-sidebar-scrollbar)' }}>
            <Icon name="search" size={14} />
          </span>
          <input
            type="search"
            value={navSearch}
            onChange={(e) => setNavSearch(e.target.value)}
            placeholder="Buscar módulo…"
            aria-label="Buscar módulo"
            style={{
              width: '100%', boxSizing: 'border-box', height: 32, padding: '0 10px 0 30px',
              borderRadius: 8, border: '1px solid transparent', outline: 'none',
              background: 'rgba(255,255,255,0.06)', color: 'var(--color-sidebar-text)',
              fontSize: 12.5, fontFamily: 'var(--font-body)',
            }}
          />
        </div>
      </div>

      {/* Nav */}
      <nav
        className="sidebar-themed-scrollbar"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 15px', display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        {searchResults ? (
          searchResults.length > 0 ? (
            searchResults.map((item) => (
              <NavItemLink key={item.path} item={item} onNavClick={onNavClick} />
            ))
          ) : (
            <p style={{ fontSize: 12.5, color: 'var(--color-sidebar-text-muted)', padding: '10px 12px', margin: 0 }}>
              Sin resultados
            </p>
          )
        ) : (
          groupedItems.map(({ group, items }) => {
          if (group === 'general') {
            // Sin encabezado ni contenedor: los ítems transversales van sueltos
            // al tope del nav, igual que antes de la agrupación.
            return items.map((item) => (
              <NavItemLink key={item.path} item={item} onNavClick={onNavClick} />
            ));
          }

          // Se fuerza expandido si es el grupo de la ruta activa, así el item
          // activo nunca queda oculto por la preferencia de colapso.
          const isCollapsed = collapsed.includes(group) && group !== activeGroup;
          return (
            <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                aria-expanded={!isCollapsed}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '14px 12px 6px',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--color-sidebar-scrollbar)',
                }}
              >
                {GROUP_LABELS[group]}
                <span
                  style={{
                    display: 'flex',
                    transition: 'transform 0.15s',
                    transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                  }}
                >
                  <Icon name="chevron-down" size={14} />
                </span>
              </button>
              {!isCollapsed &&
                items.map((item) => (
                  <NavItemLink key={item.path} item={item} onNavClick={onNavClick} />
                ))}
            </div>
          );
          })
        )}
      </nav>

      {/* User footer */}
      <div style={{ padding: '12px 15px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderRadius: 12, background: 'var(--color-sidebar-footer)' }}>
          <UserAvatar size={32} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-surface-tint)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.firstName} {user?.lastName}
            </p>
            <p style={{ fontSize: 11, color: 'var(--color-sidebar-text-muted)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </p>
          </div>
          <button
            onClick={logout}
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-sidebar-text-muted)', display: 'flex', padding: 4, borderRadius: 6,
              transition: 'color 0.15s',
            }}
            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-surface-tint)'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-sidebar-text-muted)'; }}
          >
            <Icon name="logout" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close drawer on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Trap scroll behind overlay
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <div className="layout-root" style={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>

      {/* ── Desktop sidebar (≥768px) ── */}
      <aside style={{
        width: 236, flexShrink: 0, background: 'var(--color-sidebar)',
        display: 'flex', flexDirection: 'column',
      }} className="layout-sidebar-desktop">
        <SidebarContent user={user} logout={logout} />
      </aside>

      {/* ── Mobile overlay ── */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 40, backdropFilter: 'blur(2px)',
          }}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile drawer ── */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: 280, background: 'var(--color-sidebar)', zIndex: 50,
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
        }}
        className="layout-drawer-mobile"
      >
        <SidebarContent
          user={user}
          logout={logout}
          onNavClick={() => setDrawerOpen(false)}
        />
      </div>

      {/* ── Main area ── */}
      <div className="layout-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Mobile topbar */}
        <header style={{
          height: 56, background: 'var(--color-topbar)', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', flexShrink: 0,
        }} className="layout-topbar-mobile">
          <button
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label={drawerOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={drawerOpen}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-text)', padding: 6, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name={drawerOpen ? 'x' : 'menu'} size={22} />
          </button>

          <img src="/mirasoles-logo.png" alt="Mirasoles" style={{ height: 24, width: 'auto', filter: 'var(--logo-filter)' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThemeToggle size={32} />
            <NotificationBell />
            <UserAvatar size={32} />
          </div>
        </header>

        {/* Desktop topbar */}
        <header style={{
          height: 66, background: 'var(--color-topbar)', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 28px', flexShrink: 0,
        }} className="layout-topbar-desktop">
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--color-text)', letterSpacing: '-0.01em' }}>
              {pageTitle(location.pathname).title}
            </div>
            {pageTitle(location.pathname).sub && (
              <div style={{ fontSize: 12, color: 'var(--color-sidebar-scrollbar)', marginTop: 1 }}>
                {pageTitle(location.pathname).sub}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ThemeToggle />
            <NotificationBell />
            <UserAvatar size={34} />
          </div>
        </header>

        <main className="themed-scrollbar layout-main" style={{ flex: 1, overflowY: 'auto', background: 'var(--color-app)' }}>
          <Outlet />
        </main>
      </div>

      <NotificationToastContainer />
      <ToastContainer />

      <style>{`
        .sidebar-themed-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: var(--color-sidebar-scrollbar) transparent;
        }
        .sidebar-themed-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .sidebar-themed-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-themed-scrollbar::-webkit-scrollbar-thumb {
          background-color: var(--color-sidebar-scrollbar);
          border-radius: 10px;
        }
        @media (min-width: 768px) {
          .layout-sidebar-desktop { display: flex !important; }
          .layout-topbar-mobile { display: none !important; }
          .layout-topbar-desktop { display: flex !important; }
          .layout-drawer-mobile { display: none !important; }
        }
        @media (max-width: 767px) {
          .layout-sidebar-desktop { display: none !important; }
          .layout-topbar-mobile { display: flex !important; }
          .layout-topbar-desktop { display: none !important; }
        }
      `}</style>
    </div>
  );
}
